import React, { useContext } from 'react';
import { styled } from '@mui/material';
import { useParentSize } from '@visx/responsive';
import { LegendType } from './Chart.types';

export const LegendTypeDataSelector = {
  [LegendType.linearTop]: `[data-legend-type="${LegendType.linearTop}"]`,
  [LegendType.linearBottom]: `[data-legend-type="${LegendType.linearBottom}"]`,
  [LegendType.tableRight]: `[data-legend-type="${LegendType.tableRight}"]`,
  [LegendType.none]: `[data-legend-type="${LegendType.none}"]`,
} as const;

const StyledWrapper = styled('div')({
  height: '100%',
  width: '100%',
  overflow: 'hidden',

  [`&:not(${LegendTypeDataSelector[LegendType.none]})`]: {
    display: 'grid',
  },

  [`&${LegendTypeDataSelector[LegendType.linearTop]}`]: {
    gridTemplateAreas: "'legend' 'chart'",
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'auto 1fr',
  },

  [`&${LegendTypeDataSelector[LegendType.linearBottom]}`]: {
    gridTemplateAreas: "'chart' 'legend'",
    gridTemplateColumns: '1fr',
    gridTemplateRows: '1fr auto',
  },

  [`&${LegendTypeDataSelector[LegendType.tableRight]}`]: {
    gridTemplateAreas: "'chart legend'",
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: '1fr',
    alignContent: 'center',
  },
});

export type ChartWrapperProps = {
  legendType: LegendType;
};

type ChartWrapperContextType = {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
};

const ChartWrapperContext = React.createContext<ChartWrapperContextType | undefined>(undefined);

export const ChartWrapper = ({ children, legendType }: React.PropsWithChildren<ChartWrapperProps>) => {
  const { parentRef: wrapperRef, width, height } = useParentSize<HTMLDivElement>({ debounceTime: 50 });

  // The circular chart only ever needs a column as wide as it's tall (a square) - never more. Measuring the
  // wrapper directly and computing this in JS (rather than relying on CSS `max-content`/`aspect-ratio` grid-track
  // sizing, which is unreliable across browsers when the row height itself comes from the same grid) lets the
  // legend column reliably expand into whatever horizontal space the circle doesn't need on wide/short widgets,
  // while never shrinking the chart column below the space the circle actually needs on narrow/tall ones.
  const chartColumnWidth = legendType === LegendType.tableRight && width > 0 && height > 0 ? Math.min(height, width / 2) : undefined;

  return (
    <ChartWrapperContext.Provider value={React.useMemo(() => ({ wrapperRef }), [wrapperRef])}>
      <StyledWrapper
        ref={wrapperRef}
        data-legend-type={legendType}
        style={chartColumnWidth !== undefined ? { gridTemplateColumns: `${chartColumnWidth}px 1fr` } : undefined}>
        {children}
      </StyledWrapper>
    </ChartWrapperContext.Provider>
  );
};

export const useChartWrapperContext = () => {
  const ctx = useContext(ChartWrapperContext);
  if (!ctx) throw new Error('useChartWrapperContext must be used within a ChartWrapper');
  return ctx;
};
